/**
 * RouteMap — Google Maps view of a crew route with numbered stop markers and route polyline.
 *
 * Props:
 *   stops           {Array}    - ordered stop array with latitude, longitude, name, actionType
 *   selectedIndex   {number}   - currently selected stop index (highlighted marker)
 *   onSelectStop    {function} - called with stop index when marker is clicked
 *   startLocation   {object}   - { latitude, longitude, label } or null
 *   endLocation     {object}   - { latitude, longitude, label } or null
 */

import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import { GoogleMap, LoadScript, Marker, Polyline } from '@react-google-maps/api'

const GMAP_KEY = process.env.REACT_APP_GMAPKEY
const PHOENIX_CENTER = { lat: 33.57, lng: -112.09 }

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  noKey: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: theme.palette.text.secondary,
  },
}))

const ACTION_COLORS = {
  work_ticket_visit: '#1565C0',
  maintenance_visit: '#2E7D32',
  irrigation_inspection: '#0277BD',
  herbicide_application: '#F57F17',
  site_audit: '#6A1B9A',
  site_walk: '#4527A0',
  lead_capture: '#00838F',
  customer_meeting: '#4E342E',
  issue_resolution: '#C62828',
  generic: '#546E7A',
}

const mapContainerStyle = { width: '100%', height: '100%' }

const mapOptions = {
  mapTypeId: 'hybrid',
  mapTypeControl: true,
  mapTypeControlOptions: { position: 3 }, // TOP_RIGHT
  streetViewControl: false,
  fullscreenControl: true,
  zoomControl: true,
}

/**
 * Build a numbered marker icon using Google Charts API (simple, no custom SVG needed).
 */
function makeMarkerIcon(number, color, isSelected) {
  const size = isSelected ? 42 : 34
  const scale = isSelected ? 1.3 : 1
  return {
    url: `https://chart.googleapis.com/chart?chst=d_map_pin_letter&chld=${number}|${color.replace('#', '')}|FFFFFF`,
    scaledSize: typeof google !== 'undefined' ? new google.maps.Size(size, size) : undefined,
  }
}

function RouteMap({ stops, selectedIndex, onSelectStop, startLocation, endLocation, baseUrl }) {
  const classes = useStyles()

  // Filter stops with valid coordinates
  const routableStops = useMemo(() => {
    return (stops || []).filter(s => s.latitude && s.longitude)
  }, [stops])

  // Build straight-line fallback path (start → stops → end)
  const straightLinePath = useMemo(() => {
    const path = []
    if (startLocation && startLocation.latitude && startLocation.longitude) {
      path.push({ lat: startLocation.latitude, lng: startLocation.longitude })
    }
    routableStops.forEach(s => {
      path.push({ lat: s.latitude, lng: s.longitude })
    })
    if (endLocation && endLocation.latitude && endLocation.longitude) {
      path.push({ lat: endLocation.latitude, lng: endLocation.longitude })
    }
    return path
  }, [routableStops, startLocation, endLocation])

  // Fetch road-snapped geometry from Mapbox via our server endpoint
  const [roadGeometry, setRoadGeometry] = useState([])
  const [loadingGeometry, setLoadingGeometry] = useState(false)
  const geometryRequestRef = useRef(0)

  useEffect(() => {
    if (straightLinePath.length < 2 || !baseUrl) {
      setRoadGeometry([])
      return
    }

    const requestId = ++geometryRequestRef.current
    setLoadingGeometry(true)

    // Build [lon, lat] pairs for Mapbox
    const coordinates = straightLinePath.map(p => [p.lng, p.lat])

    axios.post(`${baseUrl}/api/fleet/route-geometry`, { coordinates }, { timeout: 20000 })
      .then(res => {
        if (requestId !== geometryRequestRef.current) return // stale request
        const geo = (res.data && res.data.geometry) || []
        setRoadGeometry(geo)
      })
      .catch(err => {
        if (requestId !== geometryRequestRef.current) return
        console.warn('[RouteMap] road geometry unavailable, using straight lines:', err.message)
        setRoadGeometry([])
      })
      .finally(() => {
        if (requestId === geometryRequestRef.current) setLoadingGeometry(false)
      })
  }, [straightLinePath, baseUrl])

  // Use road geometry if available, fall back to straight lines
  const polylinePath = roadGeometry.length >= 2 ? roadGeometry : straightLinePath

  // Center map on stops
  const center = useMemo(() => {
    if (routableStops.length > 0) {
      const avgLat = routableStops.reduce((s, p) => s + p.latitude, 0) / routableStops.length
      const avgLng = routableStops.reduce((s, p) => s + p.longitude, 0) / routableStops.length
      return { lat: avgLat, lng: avgLng }
    }
    if (startLocation && startLocation.latitude) {
      return { lat: startLocation.latitude, lng: startLocation.longitude }
    }
    return PHOENIX_CENTER
  }, [routableStops, startLocation])

  const handleMarkerClick = useCallback((index) => {
    if (onSelectStop) onSelectStop(index)
  }, [onSelectStop])

  if (!GMAP_KEY) {
    return (
      <div className={classes.noKey}>
        <Typography variant="body2">Google Maps API key not configured</Typography>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <LoadScript googleMapsApiKey={GMAP_KEY}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={routableStops.length > 0 ? 11 : 10}
          options={mapOptions}
        >
          {/* Start location marker */}
          {startLocation && startLocation.latitude && (
            <Marker
              position={{ lat: startLocation.latitude, lng: startLocation.longitude }}
              label={{ text: 'S', color: '#FFFFFF', fontWeight: 'bold', fontSize: '12px' }}
              title={startLocation.label || 'Start'}
              icon={{
                url: `https://chart.googleapis.com/chart?chst=d_map_pin_letter&chld=S|4CAF50|FFFFFF`,
              }}
            />
          )}

          {/* Stop markers */}
          {routableStops.map((stop, i) => {
            // Find the original index in the full stops array
            const originalIndex = (stops || []).indexOf(stop)
            const isSelected = originalIndex === selectedIndex
            const color = ACTION_COLORS[stop.actionType] || ACTION_COLORS.generic

            return (
              <Marker
                key={stop.stopId || stop._id || `stop-${i}`}
                position={{ lat: stop.latitude, lng: stop.longitude }}
                title={`${i + 1}. ${stop.name || 'Stop'}`}
                onClick={() => handleMarkerClick(originalIndex)}
                label={{
                  text: String(i + 1),
                  color: '#FFFFFF',
                  fontWeight: 'bold',
                  fontSize: isSelected ? '14px' : '11px',
                }}
                zIndex={isSelected ? 1000 : i}
                animation={isSelected ? 1 : undefined} // BOUNCE = 1
              />
            )
          })}

          {/* End location marker (if different from start) */}
          {endLocation && endLocation.latitude && (
            !startLocation || endLocation.latitude !== startLocation.latitude || endLocation.longitude !== startLocation.longitude
          ) && (
            <Marker
              position={{ lat: endLocation.latitude, lng: endLocation.longitude }}
              title={endLocation.label || 'End'}
              icon={{
                url: `https://chart.googleapis.com/chart?chst=d_map_pin_letter&chld=E|F44336|FFFFFF`,
              }}
            />
          )}

          {/* Route polyline — solid blue for road geometry, dashed gray for straight lines */}
          {polylinePath.length >= 2 && (
            <Polyline
              path={polylinePath}
              options={{
                strokeColor: roadGeometry.length >= 2 ? '#1E88E5' : '#90A4AE',
                strokeOpacity: roadGeometry.length >= 2 ? 0.85 : 0.5,
                strokeWeight: roadGeometry.length >= 2 ? 4 : 3,
                geodesic: true,
              }}
            />
          )}
        </GoogleMap>
      </LoadScript>
    </div>
  )
}

export default RouteMap
